import * as React from 'react'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { Redirect, Route, RouteProps, Switch } from 'react-router-dom'

import { Header } from './components/header'
import { RewardsFetcher } from './components/rewards-fetcher'
import { AddAccountPaths, MasaDashboardPaths, Paths } from './config/routes'
import { LOCAL_STORAGE_CONTEXT } from './contexts/local-storage'
import { useUser } from './contexts/user/user-context'
import { useBrave } from './hooks/use-brave/use-brave'
import { authService } from './lib/auth-service'
import { Notify } from './lib/helpers'
import { AddAccount } from './pages/add-account'
import { MasaDashboard } from './pages/masa-dashboard/masa-dashbaord'
import { NotFound } from './pages/not-found'
import { Onboarding } from './pages/onboarding'
import { ResetPassword } from './pages/reset-password'
import { SignIn } from './pages/sign-in'
import { SignUp } from './pages/sign-up'
import { DashboardRouter } from './routers/dashboard'
import { SettingsRouter } from './routers/settings'

function MainAppRouter() {
    const isBrave = useBrave()
    const { get, save } = React.useContext(LOCAL_STORAGE_CONTEXT)

    useEffect(() => {
        if (isBrave) {
            const braveWasNotified = get('brave')
            if (!braveWasNotified) {
                save('brave', true)
                Notify(
                    'warning',
                    'Brave Browser detected',
                    'Help us improve the app, please put down your shields.',
                )
            }
        }
    }, [isBrave, save, get])
    return (
        <>
            <Header />

            <Switch>
                <PrivateRoute path={Paths.ONBOARDING}>
                    <Onboarding />
                </PrivateRoute>
                <PrivateRoute path={AddAccountPaths.ADD_ACCOUNT}>
                    <AddAccount />
                </PrivateRoute>
                <PrivateRoute path={Paths.MAIN_APP}>
                    <DashboardRouter />
                </PrivateRoute>
                <Route>
                    <NotFound />
                </Route>
            </Switch>
        </>
    )
}

export function App() {
    return (
        <>
            <Toaster position='bottom-right' />
            <RewardsFetcher />
            <Switch>
                <Route path={Paths.ROOT} exact={true}>
                    <Redirect
                        to={{
                            pathname: Paths.DASHBOARD,
                        }}
                    />
                </Route>

                <PrivateRoute path={MasaDashboardPaths.ROOT}>
                    <AdminRoute>
                        <MasaDashboard />
                    </AdminRoute>
                </PrivateRoute>
                <Route path={Paths.SIGN_UP}>
                    <SignUp />
                </Route>
                <Route path={Paths.SIGN_IN}>
                    <SignIn />
                </Route>
                <Route path={Paths.RESET_PASSWORD}>
                    <ResetPassword />
                </Route>
                <PrivateRoute path={Paths.MAIN_APP}>
                    <MainAppRouter />
                </PrivateRoute>
                <PrivateRoute path={Paths.SETTINGS}>
                    <SettingsRouter />
                </PrivateRoute>
                <Route>
                    <NotFound />
                </Route>
            </Switch>
        </>
    )
}

/**
 * Route to redirect you to the sign up page if your aren't logged in
 */
export function PrivateRoute({ children, ...rest }: RouteProps) {
    const [authenticated, setAuthenticated] = React.useState<boolean | null>(
        null,
    )

    React.useEffect(() => {
        authService
            .getUser()
            .then(() => setAuthenticated(true))
            .catch(() => setAuthenticated(false))
    }, [])

    return (
        <Route
            {...rest}
            render={({ location }) => {
                if (authenticated === null) {
                    return null
                }

                if (authenticated) {
                    return children
                }

                return (
                    <Redirect
                        to={{
                            pathname: Paths.SIGN_IN,
                            state: { from: location },
                        }}
                    />
                )
            }}
        />
    )
}

/**
 * Route to redirect you to the dashboad if you try to access an admin route without admin access
 */
export function AdminRoute({ children, ...rest }: RouteProps) {
    //TODO: Probably create a type for this
    const { userProfile }: any = useUser()

    const activeRole = React.useMemo(() => {
        if (userProfile && userProfile.getUserProfile) {
            return userProfile.getUserProfile.activeRole
        } else {
            return ''
        }
    }, [userProfile])
    console.log('ActiveRole => ', activeRole)
    return (
        <Route
            {...rest}
            render={({ location }) => {
                if (activeRole === '') {
                    return null
                }

                if (activeRole.toLowerCase() === 'admin') {
                    return children
                }

                return (
                    <Redirect
                        to={{
                            pathname: Paths.DASHBOARD,
                            state: { from: location },
                        }}
                    />
                )
            }}
        />
    )
}
